import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UnifiedMetric } from '@/types/unifiedLibrary';
import { 
  Calculator, 
  Database, 
  Calendar, 
  Building, 
  CheckCircle, 
  AlertCircle, 
  Target,
  Recycle,
  Globe,
  Users,
  Shield,
  Flame,
  FileText,
  Hash,
  Settings
} from 'lucide-react';

interface MetricDetailsModalProps {
  metric: UnifiedMetric | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MetricDetailsModal = ({ metric, isOpen, onClose }: MetricDetailsModalProps) => {
  console.log('MetricDetailsModal render:', { metric: metric?.name, isOpen });
  if (!metric) return null;

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Environmental': return <Globe className="h-4 w-4" />;
      case 'Social': return <Users className="h-4 w-4" />;
      case 'Governance': return <Shield className="h-4 w-4" />;
      case 'Economic': return <Target className="h-4 w-4" />;
      case 'Material': return <Flame className="h-4 w-4" />;
      default: return <Database className="h-4 w-4" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Hard': return 'bg-orange-100 text-orange-800';
      case 'Expert': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'Scope 1': return 'bg-red-100 text-red-800';
      case 'Scope 2': return 'bg-orange-100 text-orange-800';
      case 'Scope 3': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getCategoryIcon(metric.category)}
            {metric.name}
          </DialogTitle>
          <DialogDescription>
            {metric.description}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh]">
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Category</div>
                    <Badge variant="outline" className="flex items-center gap-1">
                      {getCategoryIcon(metric.category)}
                      {metric.category}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Subcategory</div>
                    <Badge variant="secondary">{metric.subcategory}</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Unit</div>
                    <Badge variant="outline">{metric.unit}</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Data Type</div>
                    <Badge variant="outline">{metric.data_type}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assessment & Difficulty */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Assessment & Difficulty
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Difficulty</div>
                    <Badge className={getDifficultyColor(metric.difficulty || 'Unknown')}>
                      {metric.difficulty || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Mandatory</div>
                    <div className="flex items-center gap-2">
                      {metric.mandatory ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                      )}
                      <span className="text-sm">
                        {metric.mandatory ? 'Required' : 'Optional'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Frequency</div>
                    <Badge variant="outline">{metric.frequency || 'Not specified'}</Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-600">Calculation Method</div>
                    <div className="text-sm text-gray-700">
                      {metric.calculation_method || 'Not specified'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GHG Scope & Emissions */}
            {metric.scope && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Recycle className="h-5 w-5" />
                    GHG Scope & Emissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-600">GHG Scope</div>
                      <Badge className={getScopeColor(metric.scope)}>
                        {metric.scope}
                      </Badge>
                    </div>
                    {metric.emission_factor && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Emission Factor</div>
                        <div className="text-sm text-gray-700">
                          {metric.emission_factor} kg CO₂e/{metric.unit}
                        </div>
                      </div>
                    )}
                    {metric.calorific_value && (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Calorific Value</div>
                        <div className="text-sm text-gray-700">
                          {metric.calorific_value} MJ/{metric.unit}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Data Sources */}
            {metric.data_source && Array.isArray(metric.data_source) && metric.data_source.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Data Sources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {metric.data_source.map((source, index) => (
                      <Badge key={index} variant="outline" className="bg-gray-50">
                        {source}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* International Standards */}
            {metric.standards && Object.keys(metric.standards).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">International Standards & Requirements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metric.standards).map(([standardName, standardCodes], index) => (
                      <div key={index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="font-medium">
                            {standardName}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(standardCodes) ? standardCodes.map((code, codeIndex) => (
                            <Badge key={codeIndex} variant="secondary" className="text-xs">
                              {code}
                            </Badge>
                          )) : (
                            <Badge variant="secondary" className="text-xs">
                              {standardCodes}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Emission Factors & Conversion Rates */}
            {metric.factors && Object.keys(metric.factors).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Emission Factors & Conversion Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metric.factors).map(([factorName, factorValue], index) => (
                      <div key={index} className="border rounded-lg p-3 bg-blue-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-sm">{factorName}</span>
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">{JSON.stringify(factorValue)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Industry & Tags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {metric.industry && metric.industry.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Applicable Industries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {metric.industry.map((industry, index) => (
                        <Badge key={index} variant="outline" className="bg-green-50 text-green-700">
                          {industry}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {metric.tags && metric.tags.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Tags</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {metric.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Additional Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metric.source && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-600">Source</div>
                      <div className="text-sm text-gray-700">{metric.source}</div>
                    </div>
                  )}
                  {metric.target_type && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-600">Target Type</div>
                      <div className="text-sm text-gray-700">{metric.target_type}</div>
                    </div>
                  )}
                  {metric.currency && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-600">Currency</div>
                      <div className="text-sm text-gray-700">{metric.currency}</div>
                    </div>
                  )}
                  {metric.material_id && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-600">Material ID</div>
                      <div className="text-sm text-gray-700 font-mono">{metric.material_id}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}; 